// eslint-disable-next-line import/no-extraneous-dependencies -- this is test code, so it's appropriate to list as devDependency
import {
  type DefaultBodyType,
  type ResponseComposition,
  type ResponseFunction,
  type ResponseResolver,
  type ResponseTransformer,
  rest as origRest,
  type RestContext,
  type RestRequest,
} from 'msw';

function createDelayedResponseFunction(
  res: ResponseFunction<DefaultBodyType>,
  ctx: RestContext
): ResponseFunction<DefaultBodyType> {
  return (...transformers: ResponseTransformer<DefaultBodyType>[]) =>
    res(
      ...transformers,
      ctx.delay(25) // transformers are processed in reverse order, so adding at end to allow overriding
    );
}

/**
 * Drop-in replacement for MSW's `rest` object that adds a delay to all
 * resolvers. The delay can be overridden or removed by specifying a delay in
 * the consuming code, e.g.
 *
 * ```js
 * const DURATION = 1000; // or `0` for no delay
 * mswDelayedRest.get('/path', (req, res, ctx) => res(ctx.delay(DURATION), ctx.status(204)));
 * ```
 */
export default Object.fromEntries(
  Object.entries(origRest).map(([method, handler]) => [
    method,
    (
      path: string,
      resolver: ResponseResolver<RestRequest, RestContext, DefaultBodyType>
    ) =>
      handler(path, (req, res, ctx) => {
        const delayResolver: ResponseComposition<DefaultBodyType> =
          Object.assign(createDelayedResponseFunction(res, ctx), {
            networkError: res.networkError,
            once: createDelayedResponseFunction(res.once, ctx),
          });

        return resolver(req, delayResolver, ctx);
      }),
  ])
);
